import { ICurrentUser } from '@dadohr/shared/domainTypes/currentUser';
import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  AudienceWithCompletionCriteria,
  IMomentMailErrorData,
  IMomentMailEventReport,
  IMomentReportData,
  IMomentTemplate,
  isContentBlock,
  isLayoutBlock,
  isSubTaskTemplateBlock,
  ISubTask,
  MomentInstanceStatus,
  MomentTemplateStatus,
  PARTICIPANT_ACTOR_KEY,
  SubTaskStatus,
  TaskBlockTemplate
} from '@dadohr/shared/domainTypes/experience';
import { PREVIEW_MAIL_PREFIX } from '@dadohr/shared/domainTypes/mail';
import { IOrganization } from '@dadohr/shared/domainTypes/org';
import { IUser } from '@dadohr/shared/domainTypes/user';
import { CF, FS } from '@dadohr/shared/versions';
import assertNever from 'assert-never';
import { compact, keyBy } from 'lodash';
import moment from 'moment-timezone';
import shortid from 'shortid';
import {
  toDenormalizedContentBlock,
  toDenormalizedLayoutBlock,
  toDenormalizedSubTask
} from '../../../services/experiences/instance';
import { downloadFile, toCsv } from '../../../services/file';
import {
  LoadingValue,
  refreshTimestamp,
  removeDoc,
  setDoc,
  store,
  updateDataInDoc,
  useMappedLoadingValue
} from '../../../services/firebase';
import {
  callFirebaseFunction,
  useFirebaseFunction
} from '../../../services/firebaseFunctions';
import {
  CollectionListener,
  createCollectionListenerStore,
  useCollectionListener
} from '../../../services/firecache/collectionListener';
import {
  createDocumentListenerGetter,
  useDocumentListener
} from '../../../services/firecache/documentListener';
import { toDenormalizedOrg } from '../../../services/orgs';
import {
  ExperienceReplacements,
  interpolateExperienceText,
  InterpolationDefaultOptions,
  userToInterpolation
} from '../../../services/set/interpolation';
import {
  fromMoment,
  now,
  ONE_MINUTE,
  Timestamp,
  toMoment
} from '../../../services/time';
import {
  matchUser,
  toDenormalizedUser,
  toFullName
} from '../../../services/user';
import { EMPTY_RULE_GROUP } from '../../Experiences/components/RuleBuilder';
import { toMomentInstanceDoc, toMomentTemplateDoc } from './documents';

const templateCollection = () => store().collection(FS.momentTemplates);

const getMomentTemplateListenerByOrgId = createCollectionListenerStore(
  (orgId) =>
    new CollectionListener(
      templateCollection().where('orgId', '==', orgId),
      toMomentTemplateDoc
    )
);

const getMomentTemplateListenerById = createDocumentListenerGetter(
  (id) => templateCollection().doc(id),
  toMomentTemplateDoc
);

export const useMomentTemplatesByOrgId = (orgId: string) => {
  return useCollectionListener(getMomentTemplateListenerByOrgId(orgId));
};

export const useMomentTemplateById = (tplId: string) => {
  return useDocumentListener(getMomentTemplateListenerById(tplId));
};

const DELIMITER = '|||';

const getMomentInstanceListenerByUserId = createCollectionListenerStore(
  (orgIdAndUserId) => {
    const [orgId, userId] = orgIdAndUserId.split(DELIMITER);
    return new CollectionListener(
      store()
        .collection(FS.momentInstances)
        .where('orgId', '==', orgId)
        .where('userId', '==', userId),
      toMomentInstanceDoc
    );
  }
);

export const useMomentInstancesByUserId = (orgId: string, userId: string) => {
  return useCollectionListener(
    getMomentInstanceListenerByUserId([orgId, userId].join(DELIMITER))
  );
};

export const getEmptyAudience = (getRandomColor: () => string) => {
  const emptyAudience: AudienceWithCompletionCriteria = {
    id: shortid(),
    name: '',
    color: getRandomColor(),
    blocks: [],
    rule: { ...EMPTY_RULE_GROUP },
    completionCriteria: []
  };
  return emptyAudience;
};

export const getEveryoneAudience = () => {
  const everyoneAudience: AudienceWithCompletionCriteria = {
    id: shortid(),
    blocks: [],
    rule: 'EVERYONE',
    name: '',
    color: '',
    completionCriteria: []
  };
  return everyoneAudience;
};

export const getEmptyMomentTemplate = (
  orgId: string,
  currentUser: ICurrentUser,
  tz: string
): Doc<IMomentTemplate> => {
  return {
    id: shortid(),
    collection: FS.momentTemplates,
    data: {
      orgId,
      title: 'Unnamed Moment',
      status: MomentTemplateStatus.DRAFT,
      subject: '',
      content: {
        blocks: [],
        audiences: [
          {
            id: shortid(),
            blocks: [],
            rule: 'EVERYONE',
            name: '',
            color: '',
            completionCriteria: []
          }
        ]
      },
      categories: [],
      from: {
        name: toFullName(currentUser),
        email: currentUser.email
      },
      replyTo: {
        email: currentUser.email
      },
      sendDate: {
        type: 'FIXED_DATE',
        at: fromMoment(moment().startOf('d').hour(9).add(1, 'week'))
      },
      tz,
      createdAt: now(),
      createdBy: currentUser.id,
      updatedAt: now(),
      updatedBy: currentUser.id
    }
  };
};

export const duplicateMomentTemplate = async (
  d: Doc<IMomentTemplate>,
  currentAdminId: string
) => {
  const newAudiences = d.data.content.audiences.map((a) => ({
    ...a,
    id: shortid()
  }));
  const newSendDateAt = toMoment(d.data.sendDate.at).isBefore()
    ? fromMoment(moment().startOf('d').hour(9).add(1, 'week'))
    : d.data.sendDate.at;

  const newMoment: Doc<IMomentTemplate> = {
    id: shortid(),
    collection: FS.momentTemplates,
    data: {
      ...d.data,
      status: MomentTemplateStatus.DRAFT,
      createdAt: now(),
      updatedAt: now(),
      createdBy: currentAdminId,
      updatedBy: currentAdminId,
      title: `[Copy] ${d.data.title}`,
      // TODO - all blocks should be properly copied and get new ids too
      // Which means that these new ids need to be featured in the audience blocks as well
      // This is also done when duplicating experience templates
      content: {
        ...d.data.content,
        audiences: newAudiences
      },
      sendDate: {
        ...d.data.sendDate,
        at: newSendDateAt
      }
    }
  };

  await setDoc(newMoment);
  return newMoment;
};

export const saveMomentTemplate = async (
  currentUserId: string,
  d: Doc<IMomentTemplate>
) => {
  d.data.updatedAt = now();
  d.data.updatedBy = currentUserId;
  const nextD = updateDataInDoc(d, {
    updatedAt: now(),
    updatedBy: currentUserId
  });
  await setDoc(nextD);
  return nextD;
};

export const updateMomentTemplateStatus = (
  currentUserId: string,
  d: Doc<IMomentTemplate>,
  nextStatus: MomentTemplateStatus.DRAFT | MomentTemplateStatus.SCHEDULED
) => {
  if (nextStatus === MomentTemplateStatus.SCHEDULED) {
    // validate -> throw a meaningful error if it's invalid
  }
  const nextD = updateDataInDoc(d, { status: nextStatus });
  return saveMomentTemplate(currentUserId, nextD);
};

export const deleteMomentTemplate = async (d: Doc<IMomentTemplate>) => {
  // Maybe disallow to delete in certain states, just to be save
  await removeDoc(d);
};

export const createNewMomentTemplate = async (currentUser: ICurrentUser) => {
  const newMomentTemplate = getEmptyMomentTemplate(
    currentUser.org.id,
    currentUser,
    currentUser.org.tz
  );
  await setDoc(newMomentTemplate);
  return newMomentTemplate;
};

export const getMomentsAudience = (
  tpl: Doc<IMomentTemplate>,
  user: Doc<IUser>,
  referenceDate: Timestamp
) => {
  return (
    tpl.data.content.audiences.find((a) => {
      return (
        !a.inactive &&
        (a.rule === 'EVERYONE' || matchUser(user, a.rule, referenceDate))
      );
    }) || null
  );
};

const collectMomentReplacements = (
  org: IOrganization,
  user: Doc<IUser>,
  referenceDate: Timestamp
): ExperienceReplacements => {
  return {
    // Experience is unused here - we just set it to defaults
    // so that we don't have to rewrite all the code and can still
    // use the other codepaths
    experience: {
      vars: {
        title: '',
        tasks: {}
      }
    },
    org: { vars: { name: org.name } },
    roles: [
      {
        key: PARTICIPANT_ACTOR_KEY,
        label: 'Participant',
        vars: userToInterpolation(
          user.data,
          org.customUserFields,
          referenceDate
        )
      }
    ]
  };
};

export const getBlocksForMomentAudience = (
  audience: AudienceWithCompletionCriteria | undefined,
  blocks: TaskBlockTemplate[]
) => {
  return compact(
    audience?.blocks.map((bId) => blocks.find((b) => b.id === bId)) || []
  );
};

export const createDenormalizedMomentForPreviewing = (
  org: IOrganization,
  tpl: Doc<IMomentTemplate>,
  user: Doc<IUser>,
  referenceDate: Timestamp
) => {
  const { from, replyTo, subject, tz, content, sendDate } = tpl.data;
  const replacements = collectMomentReplacements(org, user, referenceDate);
  const opts: InterpolationDefaultOptions = {
    dateFormat: org.dateFormats.MMDDYYYY
  };
  const audience = getMomentsAudience(tpl, user, referenceDate);
  if (!audience) {
    throw new Error('no audience');
  }
  const blocks = getBlocksForMomentAudience(audience, content.blocks);
  const subTasksByTplId = keyBy(
    compact(
      blocks.map<ISubTask | null>((b) => {
        if (!isSubTaskTemplateBlock(b)) {
          return null;
        }
        return {
          id: b.id,
          templateId: b.id,
          status: SubTaskStatus.OPEN,
          type: b.type,
          completedAt: null,
          requirementType: b.requirementType,
          parentLayoutElement: b.parentLayoutElement || null
        };
      })
    ),
    (s) => s.id
  );
  const sendAt = sendDate.at;
  const accessToken = `${PREVIEW_MAIL_PREFIX}${tpl.id}-${user.id}`;

  return {
    id: accessToken,
    collection: 'momentDenormalized',
    data: {
      accessToken,
      templateId: tpl.id,
      instanceId: accessToken,
      org: toDenormalizedOrg(org, tpl.data.overrides?.org || null),
      userId: user.id,
      user: toDenormalizedUser(user),
      status: MomentInstanceStatus.OPEN,
      from,
      replyTo,
      sendAt, // only fixed date atm
      markedAsRead: null,
      archivedBasedOnTime: false,
      tz,

      subject: interpolateExperienceText(subject, replacements, opts),
      content: {
        customCompletionCriteria: audience.completionCriteria,
        blocks: compact(
          blocks.map((b) => {
            if (isSubTaskTemplateBlock(b)) {
              // TODO problematic - this fn is also coupled very tightly to the idea of experiences
              // some arguments that are needed here are only used for subtasks we don't offer here
              // events are used to find completion dates - this is another good argument to
              // store the completion date on the subtask level as well, which would simplify this

              const s = subTasksByTplId[b.id];
              if (!s) {
                console.warn('SubTask template not found', {
                  block: b
                });
                return null;
              }
              return toDenormalizedSubTask(
                s,
                b,
                replacements,
                {
                  answersById: {},
                  calendlyEventsById: {}
                },
                opts,
                tz,
                sendAt,
                sendAt,
                [],
                {
                  [PARTICIPANT_ACTOR_KEY]: user
                }
              );
            }
            if (isContentBlock(b)) {
              return toDenormalizedContentBlock(b, replacements, opts);
            }

            if (isLayoutBlock(b)) {
              return toDenormalizedLayoutBlock(b);
            }
            return assertNever(b);
          })
        )
      }
    }
  };
};

export const getMomentReport = (tpl: Doc<IMomentTemplate>) => {
  if (tpl.data.status !== MomentTemplateStatus.COMPLETE) {
    throw new Error('Not completed yet');
  }
  return callFirebaseFunction<IMomentReportData>(CF.moments.generateReport, {
    orgId: tpl.data.orgId,
    momentTemplateId: tpl.id
  });
};

export const downloadMomentReport = async (
  tpl: Doc<IMomentTemplate>,
  opts: { format: 'csv' }
) => {
  const report = await getMomentReport(tpl);
  const { format } = opts;
  if (format !== 'csv') {
    throw new Error('unknown format');
  }
  const { headers, rows, title } = report;
  const data = await toCsv({
    fields: headers,
    data: rows.map((r) => headers.map((h) => r[h] ?? ''))
  });
  downloadFile(`[Dado] ${title} - report`, format, data);
};

const refreshMailEventReportTimestamps = (d: IMomentMailEventReport) => {
  Object.values(d.byInstance).forEach((x) => {
    x.d[1] = refreshTimestamp(x.d[1]);
    x.d[2] = refreshTimestamp(x.d[2]);
    x.o[1] = refreshTimestamp(x.o[1]);
    x.o[2] = refreshTimestamp(x.o[2]);
    x.c[1] = refreshTimestamp(x.c[1]);
    x.c[2] = refreshTimestamp(x.c[2]);
    x.b[1] = refreshTimestamp(x.b[1]);
    x.b[2] = refreshTimestamp(x.b[2]);
  });
  return d;
};
export const useMomentMailEventReport = (orgId: string, tplId: string) => {
  return useMappedLoadingValue(
    useFirebaseFunction<IMomentMailEventReport>(
      CF.moments.getMailEventReport,
      {
        orgId,
        momentTemplateId: tplId
      },
      [orgId, tplId],
      ONE_MINUTE * 10
    ),
    refreshMailEventReportTimestamps
  );
};

export const resendMoment = (
  orgId: string,
  tplId: string,
  instanceId: string
) => {
  // tpl id will be needed for potential cache invalidation
  return callFirebaseFunction(CF.moments.resendMoment, {
    orgId,
    instanceId
  });
};

export const useMomentMailErrorData = (
  orgId: string,
  tplId: string
): LoadingValue<IMomentMailErrorData[]> => {
  const [d, loading, error] = useFirebaseFunction<IMomentMailErrorData[]>(
    CF.moments.getMomentMailErrors,
    {
      orgId,
      tplId
    }
  );

  return [d, loading, error];
};
